import React, {useMemo} from 'react';
import {classes, style} from './swiper-navigation-list-view.st.css';
import {
    ListView,
    listViewCreatePrimitivesDataSource,
    ListViewRenderItemProps,
    ListViewSelectionType, NavigationOrientation,
} from "../list-view";
import {DynamicRef} from '../dynamic-ref'
import {useSwiperNavigation} from '../swiper/swiper'
import {arrayGenerate, evaluateFunction, OmitStrict} from "@wix/devzai-utils-common";
import {reactCreateMemo} from "../react-utils/react-utils";

export const SwiperNavigationListView = reactCreateMemo(function SwiperNavigationListView<E extends Element> (props: SwiperNavigationListView.Props<E>) {
    const {
        swiperElementRef,
        renderItem,
        orientation = NavigationOrientation.Horizontal,
        isRtl = false,

        className,
        ...htmlAttributes
    } = props;

    const {
        slidesCount,
        currentSlide
    } = useSwiperNavigation(swiperElementRef);

    const dataSource = useMemo(() => listViewCreatePrimitivesDataSource(arrayGenerate(slidesCount)), [slidesCount]);

    return (
        <ListView
            {...htmlAttributes}
            className={style(classes.root, className)}
            isCyclic={true}
            selectionType={ListViewSelectionType.None}
            renderItem={(renderProps) => {
                return renderItem({
                    ...renderProps,
                    isActive: renderProps.dataItem === currentSlide
                })
            }}
            orientation={orientation}
            onItemClick={event => {
                const itemIndex = event.dataItem;

                const targetElement = swiperElementRef.current?.children?.item(itemIndex);

                if (targetElement) {
                    swiperElementRef.current?.scrollTo({
                        left: evaluateFunction(() => {
                            const previousSibling = targetElement.previousElementSibling as HTMLElement | null;
                            if (previousSibling) {
                                return previousSibling.offsetLeft + previousSibling.offsetWidth * (isRtl ? -1 : 1);
                            } else {
                                return 0;
                            }
                        }),
                        behavior: 'smooth'
                    })
                }
            }}
        >
            {dataSource}
        </ListView>
    );
});

export namespace SwiperNavigationListView {

    export type ItemRenderProps = ListViewRenderItemProps<number> & {
        isActive: boolean
    };

    export interface Props<E extends Element> extends OmitStrict<ListView.Props<number>, 'children' | 'renderItem' | 'isCyclic' | 'selectionType' | 'onItemClick'> {
        swiperElementRef: DynamicRef<E>;
        renderItem: (renderProps: ItemRenderProps) => React.ReactElement;
        isRtl?: boolean;
    }
}
